import React, { PropsWithChildren, useMemo, useRef, useState } from 'react';
import { useMemoizedFn, useMount } from 'ahooks';
import { MsgStreamFinished, SubContentType, SubMessageData } from '../../types';
import { CompBlockProps, SubMsgBaseProps } from '../../types/comp-block';
import { Skeleton } from '@/common/base/skeleton';
import { IApiMessageDto, IApiSubMessageDto } from '@/services/swagger/easourcing/models';
import { MessageFeedback } from './message-feedback';
import { hiddenFeedback, subMessageConfig } from './sub-message-config';
import { useChatPageStore } from '../../store';
import { ThinkingBlock, ThinkingHeader } from './thinking-block';

interface AiMessageComponentProps {
  data: IApiMessageDto;
  isLatest?: boolean;
}
export const MountedWrapper: React.FC<
  PropsWithChildren<{
    onMounted?: () => void;
  }>
> = ({ onMounted, children }) => {
  useMount(() => {
    onMounted?.();
  });
  return children;
};

export const AiMessageComponent: React.FC<AiMessageComponentProps & CompBlockProps> = props => {
  const { data, dialogWrpperRef, sessionId, isLatest } = props;
  const onSomeAction = useChatPageStore(store => store.onSomeAtion);
  /**
   * true 表示还在等待 sse 消息，false 表示已经完成 sse 消息展示
   */
  const currentIsLoading = useChatPageStore(store => store.currentLoadingSseMessageId === data.messageId);
  const isLatestSeeMessage = useChatPageStore(store => store.lastestSseMessageId === data.messageId);
  const __scrollToBottom = useChatPageStore(store => store.scrollToBottom);
  const listWrapperRef = useRef<HTMLDivElement>(null);
  /** 鼠标是否悬停在容器上 */
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseEnter = useMemoizedFn(() => {
    setIsHovered(true);
  });

  const handleMouseLeave = useMemoizedFn(() => {
    setIsHovered(false);
  });
  const scrollToBottom = useMemoizedFn(() => {
    if (isLatestSeeMessage) {
      __scrollToBottom();
    }
  });

  /**0-未完成，1-正常完成，2-stop终止 */
  const showStopTip = useMemo(() => {
    return data.streamFinished === MsgStreamFinished.stopped && data.subMessage.length > 0;
  }, [data]);

  /**
   * true 表示已完成（SSE 连接关闭即为完成）
   * 重构后不再依赖 isTyping，仅依赖 currentIsLoading
   */
  const isFinish = useMemo(() => {
    if (!isLatestSeeMessage) return true;
    return !currentIsLoading;
  }, [isLatestSeeMessage, currentIsLoading]);

  /**
   * true 表示思考过程已完成
   * 重构后依赖子消息列表是否出现非 THOUGHT 类型，不再依赖 currentTypingIndex
   */
  const isFinishThinking = useMemo(() => {
    if (!isLatestSeeMessage || isFinish) return true;
    if (!data.subMessage.length) return false;
    // 若子消息中已出现非 THOUGHT 类型，说明思考阶段已结束
    return data.subMessage.some(sub => sub.contentType !== SubContentType.thought);
  }, [isFinish, data.subMessage, isLatestSeeMessage]);

  const isHiddenFeedback = hiddenFeedback(data);

  const renderLoading = () => {
    if (isFinish) return null;
    const showHeader = data.subMessage.length === 0;
    // 无子消息时展示骨架屏
    const showSke = showHeader;
    return (
      <React.Fragment>
        {/* 无子消息时显示思考中文案 */}
        {showHeader ? (
          <MountedWrapper onMounted={scrollToBottom}>
            <ThinkingHeader className="mb-3" isFinishThinking={false} />{' '}
          </MountedWrapper>
        ) : null}
        {showSke ? (
          <MountedWrapper onMounted={scrollToBottom}>
            <div className="flex flex-col gap-2">
              <Skeleton className="h-4 w-4/6 rounded-[4px]" />
              <Skeleton className="h-4 w-full rounded-[4px]" />
              <Skeleton className="h-4 w-11/12 rounded-[4px]" />
              <Skeleton className="h-4 w-full rounded-[4px]" />
            </div>
          </MountedWrapper>
        ) : null}
      </React.Fragment>
    );
  };

  const renderSubMsg = (item: IApiSubMessageDto) => {
    const ctx: SubMsgBaseProps = {
      messageId: data.messageId,
      isFinishThinking,
      isLastedMessage: isLatest ?? false,
      sessionId,
      msg: data,
      dialogWrpperRef: dialogWrpperRef!,
      subMsg: item as unknown as SubMessageData,
      onSomeAction,
      scrollToBottom: scrollToBottom,
    };
    const config = subMessageConfig.find(c => c.match(item));
    if (!config) return null;
    const content = config.render(item, ctx);

    return (
      <MountedWrapper key={item.subMessageId} onMounted={scrollToBottom}>
        {content}
      </MountedWrapper>
    );
  };

  // store 已按 chunk 到达顺序追加子消息，直接渲染全部，无需队列过滤
  const visibleSubMessages = data.subMessage;
  const { thoughtSubMsgs, otherSubMsgs, insightChipSubMsg } = useMemo(() => {
    const thoughtSubMsgs: Array<IApiSubMessageDto> = [];
    const otherSubMsgs: Array<IApiSubMessageDto> = [];
    let insightChipSubMsg: IApiSubMessageDto | undefined;
    visibleSubMessages.forEach(item => {
      if (item.contentType === SubContentType.thought) {
        thoughtSubMsgs.push(item);
      } else if (item.contentType === SubContentType.insightChip) {
        // 单独提取，由 MessageFeedback 之后渲染，防止在点赞/点踩上方重复渲染
        insightChipSubMsg = item;
      } else {
        otherSubMsgs.push(item);
      }
    });
    return {
      thoughtSubMsgs,
      otherSubMsgs,
      insightChipSubMsg,
    };
  }, [visibleSubMessages]);
  /** MessageFeedback 是否应该显示：isLatest 时常驻，否则 hover 时显示 */
  const showFeedback = isLatest ? true : isHovered;

  return (
    <div ref={listWrapperRef} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      {/* 由于可能有2个思考过程，错误，提取思考过程的容器组件，避免重复渲染 */}
      {thoughtSubMsgs.length ? (
        <MountedWrapper onMounted={scrollToBottom}>
          <ThinkingBlock isFinishThinking={isFinishThinking}>
            {thoughtSubMsgs.map(item => renderSubMsg(item))}
          </ThinkingBlock>
        </MountedWrapper>
      ) : null}
      {otherSubMsgs.map(item => {
        return renderSubMsg(item);
      })}

      {renderLoading()}
      {/* 如果是终止了，则显示终止提示 */}
      {showStopTip ? (
        <MountedWrapper onMounted={scrollToBottom}>
          <div className="mt-2 text-[16px] text-[#737373] leading-[22px]">You have stopped this answer</div>
        </MountedWrapper>
      ) : null}

      {!isFinish || isHiddenFeedback || data.streamFinished === MsgStreamFinished.stopped ? null : (
        <MountedWrapper onMounted={scrollToBottom}>
          {/* isLatest 时常驻显示，否则 hover 容器时才显示 */}
          <MessageFeedback
            like={data.like}
            sessionId={sessionId}
            messageId={data.messageId}
            className={showFeedback ? '' : ' opacity-0'}
          />
        </MountedWrapper>
      )}

      {/* AI 建议 Chips：仅在最新消息、输出完成、未终止、非隐藏反馈时展示，位置在点赞/点踩下方 */}
      {isLatest &&
      insightChipSubMsg &&
      isFinish &&
      !isHiddenFeedback &&
      data.streamFinished !== MsgStreamFinished.stopped ? (
        <MountedWrapper onMounted={scrollToBottom}>{renderSubMsg(insightChipSubMsg)}</MountedWrapper>
      ) : null}
    </div>
  );
};
